import {
  useDeleteBlobMutation,
  useLazyGetFindingsQuery,
  useUpdateBlobMutation,
  useUpdateFileMutation,
} from '@consigli/hooks';
import { Blob, Finding, File } from '@consigli/types';
import { useCallback } from 'react';

import { downloadBlob, getBaseName, getExtension, createFilename } from '../hooks/utils';

interface UseDocumentActionsProps {
  projectId: number;
  packageId: string;
  setBlobToDelete: (blob: Blob | null) => void;
  setFindingsToDelete: (count: number) => void;
  setIsModalOpen: (open: boolean) => void;
  setIsDeleting: (deleting: boolean) => void;
  setItemToEdit: (item: Blob | File | null) => void;
  setNewBaseName: (name: string) => void;
  setIsEditModalOpen: (open: boolean) => void;
  setIsUpdating: (updating: boolean) => void;
}

export const useDocumentActions = ({
  projectId,
  packageId,
  setBlobToDelete,
  setFindingsToDelete,
  setIsModalOpen,
  setIsDeleting,
  setItemToEdit,
  setNewBaseName,
  setIsEditModalOpen,
  setIsUpdating,
}: UseDocumentActionsProps) => {
  const [getFindings] = useLazyGetFindingsQuery();
  const [deleteBlob] = useDeleteBlobMutation();
  const [updateBlob] = useUpdateBlobMutation();
  const [updateFile] = useUpdateFileMutation();

  const handleDeleteClick = useCallback(
    async (blob: Blob) => {
      setBlobToDelete(blob);

      try {
        const findings = await getFindings({
          projectId,
          packageId,
          page: 'all',
        }).unwrap();

        const findingsToDeleteCount = findings.results.filter(
          (finding: Finding) => finding.blobId === blob.id || finding.refBlobId === blob.id,
        ).length;

        setFindingsToDelete(findingsToDeleteCount);
        setIsModalOpen(true);
      } catch (error) {
        console.error('Error fetching findings:', error);
        setFindingsToDelete(0);
        setIsModalOpen(true);
      }
    },
    [projectId, packageId, getFindings, setBlobToDelete, setFindingsToDelete, setIsModalOpen],
  );

  const handleConfirmDelete = useCallback(
    async (blobToDelete: Blob | null) => {
      if (!blobToDelete) return;

      setIsDeleting(true);
      try {
        await deleteBlob({
          projectId,
          packageId,
          blobId: blobToDelete.id,
        }).unwrap();
      } catch (error) {
        console.error('Error deleting blob:', error);
      } finally {
        setIsDeleting(false);
        setIsModalOpen(false);
        setBlobToDelete(null);
        setFindingsToDelete(0);
      }
    },
    [
      projectId,
      packageId,
      deleteBlob,
      setIsDeleting,
      setIsModalOpen,
      setBlobToDelete,
      setFindingsToDelete,
    ],
  );

  const handleDownloadClick = useCallback(async (blob: Blob) => {
    try {
      await downloadBlob(blob);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  }, []);

  const handleEditFilenameClick = useCallback(
    (item: Blob | File) => {
      setItemToEdit(item);
      const baseName = 'name' in item ? getBaseName(item.name) : getBaseName(item.filename);
      setNewBaseName(baseName);
      setIsEditModalOpen(true);
    },
    [setItemToEdit, setNewBaseName, setIsEditModalOpen],
  );

  const handleConfirmEdit = useCallback(
    async (itemToEdit: Blob | File | null, newBaseName: string) => {
      if (!itemToEdit || !newBaseName.trim()) {
        setIsEditModalOpen(false);
        setItemToEdit(null);
        setNewBaseName('');
        return;
      }

      const filename = 'name' in itemToEdit ? itemToEdit.name : itemToEdit.filename;
      const extension = getExtension(filename);
      const newFullName = createFilename(newBaseName, extension);

      // Check if the name actually changed
      if (newFullName === filename) {
        setIsEditModalOpen(false);
        setItemToEdit(null);
        setNewBaseName('');
        return;
      }

      setIsUpdating(true);
      try {
        if ('name' in itemToEdit) {
          await updateBlob({
            projectId,
            packageId,
            blobId: itemToEdit.id,
            data: { name: newFullName },
          }).unwrap();
        } else {
          await updateFile({
            projectId,
            fileId: itemToEdit.id,
            file: { originalFilename: newFullName },
          }).unwrap();
        }
        setIsEditModalOpen(false);
        setItemToEdit(null);
        setNewBaseName('');
      } catch (error: unknown) {
        console.error('Error updating filename:', error);
      } finally {
        setIsUpdating(false);
      }
    },
    [
      projectId,
      packageId,
      updateBlob,
      updateFile,
      setIsUpdating,
      setIsEditModalOpen,
      setItemToEdit,
      setNewBaseName,
    ],
  );

  return {
    handleDeleteClick,
    handleConfirmDelete,
    handleDownloadClick,
    handleEditFilenameClick,
    handleConfirmEdit,
  };
};
